"use client";

// ─────────────────────────────────────────────────────────────────────────────
// AlertPageBuddyTour — scene-2 buddy tour for /alerts/[id].
//
// Activation: the URL carries `?onboarding=1` (or any custom flag the page
// passes via `forceStart`). The handoff from the replay tour
// (/replay/[curated_id]) arrives here via the "View alert" link in the
// upload_alert success card — that link appends `?onboarding=1` before
// calling router.push, so the moment this component mounts on the alert
// page it picks up the param and fires the tour.
//
// Replaces the older `onboarding-alert-scene.tsx` implementation (which
// used a separate tour system). The unified BuddyOverlay below reuses the
// same spotlight / bubble / click-target styling the replay uses — no
// visual discontinuity between the two scenes.
//
// One-shot semantics:
//   - We strip `?onboarding=1` from the URL the instant the tour ends
//     (completes OR user escapes) via router.replace({ scroll: false }),
//     so a refresh / browser-back won't re-trigger the tour.
//   - Per-mount guard (startedRef) blocks a second tour within the same
//     component lifetime even if the URL param somehow re-appears.
//
// No DOM of its own — returns null. BuddyOverlay portals into document.body
// so layout siblings see no height/width from this mount. Safe to drop into
// the alert page tree next to <Chat>.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSidebar } from "@/components/ui/sidebar";
import { BuddyOverlay } from "./BuddyOverlay";
import { useClientBuddyScript } from "./useClientBuddyScript";
import {
  buildAlertPageTourBeats,
  buildAutomationsAlertPageTourBeats,
  buildMobileAlertPageTourBeats,
} from "./alertPageTourBeats";
import {
  AUTOMATIONS_ALERT_TOUR_QUERY_PARAM,
  buildHomeShowcaseTourUrl,
  ONBOARDING_QUERY_PARAM,
  isAutomationsAlertTourVisit,
  isOnboardingAlertVisit,
  setOnboardingResumeStage,
} from "@/lib/onboarding-constants";
import { dispatchStageComplete } from "@/lib/onboarding-tour";

interface AlertPageBuddyTourProps {
  /** Alert's display name. Used to personalize the welcome beat
   *  ("X is live"). Pass the already-resolved alert.name here —
   *  don't reach into AlertDoc shape from inside this component to
   *  keep it reusable. */
  alertName?: string | null;
  /** 1-based displayIndex of the FINAL dashboard node. Used by
   *  the dashboard-click beat to anchor the click_target ring on
   *  the right node. Defaults to 5 inside the beat builder,
   *  matching the canonical onboarding alert (`alert-437b57f91e11`,
   *  "Daily S&P 500 Edge Briefing") AFTER the cleanup pass trimmed
   *  it from 9 visible cells to 5. Pass the actual cell count from
   *  the alert page when known so other alerts (with different
   *  shapes) work too. */
  finalNodeIndex?: number;
  /** Force the tour to start regardless of URL params. Useful for
   *  wiring a manual "Replay the tour" button from settings later.
   *  Leave undefined to use the default `?onboarding=1` trigger. */
  forceStart?: boolean;
}

export function AlertPageBuddyTour({
  alertName,
  finalNodeIndex,
  forceStart,
}: AlertPageBuddyTourProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { open, openMobile, isMobile } = useSidebar();
  const startedRef = useRef(false);
  const includeSidebarOpenerRef = useRef<boolean | null>(null);

  // Decide whether to run on mount. Read URL params synchronously
  // during render so we don't flash a frame of "no tour" before the
  // effect fires.
  const isAutomationsPublicTour = isAutomationsAlertTourVisit(searchParams);
  const shouldRun = forceStart || isAutomationsPublicTour || isOnboardingAlertVisit(searchParams);
  const sidebarOpen = isMobile ? openMobile : open;
  if (includeSidebarOpenerRef.current === null) {
    includeSidebarOpenerRef.current = !sidebarOpen;
  }
  const includeSidebarOpener = includeSidebarOpenerRef.current;

  // Build beats once per alertName / finalNodeIndex change. Stable
  // reference so the hook doesn't churn on every parent re-render.
  const beats = useMemo(() => {
    if (isAutomationsPublicTour) {
      return buildAutomationsAlertPageTourBeats({
        includeSidebarOpener,
      });
    }
    if (isMobile) {
      return buildMobileAlertPageTourBeats(finalNodeIndex);
    }
    return buildAlertPageTourBeats(alertName, finalNodeIndex, {
      // Skip the "click the sidebar opener" beat when the sidebar is
      // already open — otherwise we point at the opener and tell the
      // user to click it for nothing. Mirrors the automations-public
      // branch above which already does this. `includeSidebarOpener`
      // is captured into a ref on first render so a sidebar that the
      // tour itself opens later doesn't retroactively skip the beat.
      includeSidebarOpener: shouldRun && includeSidebarOpener,
    });
  }, [
    alertName,
    finalNodeIndex,
    shouldRun,
    isAutomationsPublicTour,
    includeSidebarOpener,
    isMobile,
  ]);

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(ONBOARDING_QUERY_PARAM);
        next.delete(AUTOMATIONS_ALERT_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path =
          window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort; failure here just means a
           refresh would re-trigger the tour — not a fatal UX bug. */
      }
    },
    [router, searchParams],
  );

  const navigateToHomeTour = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(ONBOARDING_QUERY_PARAM);
        const search = next.toString();
        const path =
          window.location.pathname + (search ? `?${search}` : "");
        window.history.replaceState(window.history.state, "", path);
      } catch {
        stripQueryParam();
      }
      router.push(buildHomeShowcaseTourUrl());
    },
    [router, searchParams, stripQueryParam],
  );

  // The client-side buddy runner — owns current-beat state + advance
  // semantics. We always instantiate it (hooks can't be conditional);
  // we just feed it an empty beat list when shouldRun is false so it
  // immediately resolves to `beat: null` and BuddyOverlay renders
  // nothing.
  //
  // Both terminal paths are now identical: strip the URL param and
  // leave the user on the alert page. The previous version chained
  // into a cross-page platform tour (/automations?ptour=automations)
  // on completion, but the redesigned alert tour ends naturally on
  // the controls beat — no follow-up navigation, the user is left on
  // their fully-functional live alert page to explore freely.
  const { beat, onContinue, onExit, done } = useClientBuddyScript(
    shouldRun ? beats : [],
    {
      onComplete: () => {
        // eslint-disable-next-line no-console
        console.info("[alert-page-tour] complete");
        // Mark the matching tour-hub stage as completed. The
        // /automations → /alerts public-tour leg counts toward the
        // "automations" stage; the canonical onboarding alert flow
        // (post-research-replay handoff) counts as the "alert" stage.
        dispatchStageComplete(isAutomationsPublicTour ? "automations" : "alert");
        if (isAutomationsPublicTour) {
          // The final beat is a click_target on the sidebar home
          // logo. The user's natural click on that logo synchronously
          // navigates to "/" via app-sidebar's `navigateHomeFresh`.
          // We persist the next stage here so the home tour can
          // resume on mount even though the URL itself contains no
          // ?onboarding_home param (the natural Link doesn't add
          // one). The home page consumes this sessionStorage flag
          // and replaces the URL with `?onboarding_home=trade_ideas`.
          setOnboardingResumeStage("trade_ideas");
          return;
        }
        navigateToHomeTour();
      },
      onExit: () => {
        // eslint-disable-next-line no-console
        console.info("[alert-page-tour] dismissed");
        stripQueryParam();
      },
    },
  );

  // One-shot logging + telemetry hook point. We log "started" on the
  // first render where shouldRun flips true so the ledger shows each
  // tour start exactly once per mount, not per every re-render.
  useEffect(() => {
    if (!shouldRun || startedRef.current) return;
    startedRef.current = true;
    // eslint-disable-next-line no-console
    console.info("[alert-page-tour] starting", {
      alertName: alertName || null,
      beatCount: beats.length,
    });
  }, [shouldRun, alertName, beats.length]);

  // Persist the next stage the moment the home-handoff beat becomes
  // active — NOT in onComplete. BuddyOverlay's click_target handler
  // releases via a 75ms setTimeout (so the natural element click can
  // run first), which means by the time onComplete fires the home
  // logo's `router.push("/")` is already in flight; the home page
  // can mount + consume sessionStorage before we'd have a chance to
  // write it. Writing on beat-active sidesteps the race entirely:
  // once the user sees "Click home to continue" the resume token is
  // already in storage and survives any path back to "/".
  useEffect(() => {
    if (!isAutomationsPublicTour) return;
    if (beat?.beatId !== "alert_public_home_handoff") return;
    setOnboardingResumeStage("trade_ideas");
  }, [isAutomationsPublicTour, beat?.beatId]);

  // Defensive: if the tour was never active (shouldRun=false), or it
  // finished, return null. Saves BuddyOverlay a render cycle +
  // portal mount on pages where the tour doesn't fire.
  if (!shouldRun || done) return null;

  return (
    <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />
  );
}
